import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';

interface EmailJSResponseStatus {
  status: number;
  text: string;
}

const Support: React.FC = () => {
  const form = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    if (form.current) {
      emailjs
        .sendForm(
          'service_sq1adxo', 
          'kp-contact', 
          form.current, 
          's0Mm0OzsWSNDB4Wp0'
        )
        .then(
          (result: EmailJSResponseStatus) => {
            setIsSubmitting(false);
            setSubmitStatus({
              success: true,
              message: 'Thank you for your message! We will get back to you soon.'
            });
            setFormData({ name: '', email: '', message: '' });
          },
          (error: Error) => {
            setIsSubmitting(false);
            setSubmitStatus({
              success: false,
              message: 'There was an error sending your message. Please try again later.'
            });
            console.error('EmailJS error:', error);
          }
        );
    }
  };

  return (
    <section className="py-12 px-6">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-white text-2xl md:text-3xl font-bold mb-4 drop-shadow-md">
          Support & Contact
        </h2>
        <p className="text-white text-base mb-8 max-w-2xl mx-auto opacity-90">
          We're here to help make your experience with Quote Vibes Daily as inspiring as possible.
          If you have any questions or need assistance, please reach out.
        </p>
        
        <div className="grid grid-cols-1 align-center">
          <div className="text-left">
            {submitStatus && (
              <div className={`mb-4 p-3 rounded-md ${submitStatus.success ? 'bg-green-500/20 text-green-100' : 'bg-red-500/20 text-red-100'}`}>
                {submitStatus.message}
              </div>
            )}
            
            <form ref={form} onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-white text-sm font-medium mb-1">Name</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-white/30 bg-white/10 backdrop-blur-sm rounded-md text-white placeholder-white/50"
                  placeholder="Your name"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-white text-sm font-medium mb-1">Email</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-white/30 bg-white/10 backdrop-blur-sm rounded-md text-white placeholder-white/50"
                  placeholder="Your email"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-white text-sm font-medium mb-1">Message</label>
                <textarea 
                  id="message" 
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-white/30 bg-white/10 backdrop-blur-sm rounded-md text-white placeholder-white/50 h-32"
                  placeholder="How can we help you?"
                  required
                ></textarea>
              </div>
              
              <div>
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30 text-white font-semibold py-2 px-4 rounded-md transition-colors duration-300 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Support; 